import { useCallback, useEffect, useRef, useState } from 'react';

import type { Content } from './api.ts';
import { attentionReason } from './data.tsx';
import { navigate } from './router.tsx';

/**
 * Browser notifications for work that starts to need the operator (US-034).
 *
 * The app already polls every content for the sidebar; this watches the same
 * list and speaks up when a content goes from "nothing for you" to a reason,
 * so the operator can leave the tab while productions run. It only speaks
 * while the tab is hidden — a visible tab shows the same thing on the board —
 * and never about the first read, which would announce everything at once.
 */

/** Remembered per browser: `on` once switched on, anything else is off. */
export const NOTIFY_KEY = 'mc.notify';

/** What the bell can say about the browser's side of the switch. */
export type NotifyPermission = 'unsupported' | NotificationPermission;

function supported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function notifyPermission(): NotifyPermission {
  return supported() ? Notification.permission : 'unsupported';
}

/** Storage can throw (a private window, a full quota); off stands in. */
export function readNotify(): boolean {
  try {
    return window.localStorage.getItem(NOTIFY_KEY) === 'on';
  } catch {
    return false;
  }
}

function writeNotify(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(NOTIFY_KEY, 'on');
    else window.localStorage.removeItem(NOTIFY_KEY);
  } catch {
    // Not remembered this time; the switch still reads as the operator left it.
  }
}

/**
 * Whether the content list should keep polling in a hidden tab: only while the
 * operator has switched notifications on and the browser allows them, since a
 * hidden tab is the only time a notification is shown at all.
 */
export function notifyingWhileHidden(): boolean {
  return readNotify() && notifyPermission() === 'granted';
}

/** The board card a notification opens. */
export function notificationPath(contentId: string): string {
  return `/board?card=content:${contentId}`;
}

/**
 * Called once, in `AppShell`, with `useAppData().contents`. Compares each read
 * with the one before by content id and notifies about every content whose
 * reason went from null to something. A content that was not in the previous
 * read is not announced: it was just made, by the operator.
 */
export function useNeedsYouNotifications(contents: readonly Content[] | null): void {
  const previous = useRef<Map<string, string | null> | null>(null);

  useEffect(() => {
    if (contents === null) return;
    const reasons = new Map(contents.map((content) => [content.id, attentionReason(content)]));
    const before = previous.current;
    previous.current = reasons;
    if (before === null) return;
    if (!readNotify() || !document.hidden || notifyPermission() !== 'granted') return;
    for (const content of contents) {
      const reason = reasons.get(content.id) ?? null;
      if (reason === null || !before.has(content.id) || before.get(content.id) !== null) continue;
      show(content, reason);
    }
  }, [contents]);
}

function show(content: Content, reason: string): void {
  try {
    const notification = new Notification(content.name, { body: reason, tag: content.id });
    notification.onclick = () => {
      window.focus();
      navigate(notificationPath(content.id));
      notification.close();
    };
  } catch {
    // Some browsers only allow notifications from a service worker; nothing to show then.
  }
}

export interface NotifySwitch {
  /** Switched on here and allowed by the browser. */
  readonly on: boolean;
  readonly permission: NotifyPermission;
  readonly toggle: () => void;
}

/**
 * The bell's state. Switching on asks the browser first and stays off unless
 * it says yes; switching off only forgets the choice (a page cannot revoke a
 * permission it was given).
 */
export function useNotifySwitch(): NotifySwitch {
  const [wanted, setWanted] = useState(readNotify);
  const [permission, setPermission] = useState(notifyPermission);

  // The operator may change the site setting in another tab or the address bar.
  useEffect(() => {
    const reread = () => {
      setPermission(notifyPermission());
      setWanted(readNotify());
    };
    window.addEventListener('focus', reread);
    return () => window.removeEventListener('focus', reread);
  }, []);

  const toggle = useCallback(() => {
    if (wanted && permission === 'granted') {
      writeNotify(false);
      setWanted(false);
      return;
    }
    if (permission === 'unsupported' || permission === 'denied') return;
    const ask = permission === 'granted' ? Promise.resolve('granted' as const) : Notification.requestPermission();
    void ask.then((answer) => {
      setPermission(answer);
      writeNotify(answer === 'granted');
      setWanted(answer === 'granted');
    });
  }, [wanted, permission]);

  return { on: wanted && permission === 'granted', permission, toggle };
}
